import React,{useState} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable } from "@fortawesome/free-solid-svg-icons";
import { Form, Button } from "react-bootstrap";
import "./styles/OESettingsComponent.css";
import CheckboxWithTooltip from "./CheckboxWithTooltip";
import MaxDiffConfigComponent from "./MaxDiffConfigComponent";
import { FormattableInput } from "./FormattableInput";

const OESettingsComponent = ({
  oeParameters,
  label,
  setOeParameters,
  qtype,
  savePropertyChanges,
  selectedVariable,
}) => {
  const [showMaxDiffModal, setShowMaxDiffModal] = React.useState(false);
  const [maxDiffDesign, setMaxDiffDesign] = useState({
    method: "create",
    itemsPerSet: 5,
    tasksPerRespondent: 10,
    designVersions: 3,
    designData: "",
    designType: "balanced"
  });
  
  // Handler for all changes - directly update and save
  const handleChange = (property, value) => {
    const newParams = {
      ...oeParameters,
      [property]: value
    };
    
    // Handle exclusive checkboxes
    const exclusiveRankingOptions = ['isDropDown', 'isClick', 'isNumberRank'];
    
    if (exclusiveRankingOptions.includes(property) && value === true) {
      // Uncheck other ranking options
      exclusiveRankingOptions.forEach(opt => {
        if (opt !== property) {
          newParams[opt] = false;
        }
      });
    }
    
    // Handle other exclusivity rules
    if (property === 'isDrag' && value === true) {
      newParams.isSlider = false;
    } else if (property === 'isSlider' && value === true) {
      newParams.isDrag = false;
    }
    
    savePropertyChanges(newParams, "oeParameters");
  };

  // Special handler for MaxDiff
  const handleMaxDiffChange = (e) => {
    const isChecked = e.target.checked;
    handleChange('isMaxDiff', isChecked);
    if (isChecked) {
      setShowMaxDiffModal(true);
    }
  };

  // Handler for MaxDiff configuration updates
  const handleMaxDiffConfigUpdate = (config) => {
    const newParams = {
      ...oeParameters,
      ...config
    };
    setOeParameters(newParams);
    savePropertyChanges(newParams, "oeParameters");
  };

  return (
    <div className="oe-settings-section">
      {/* Header */}
      <div className="oe-header">
        <h4>{label}</h4>
        {selectedVariable && (
          <div className="oe-variable-info">
            <small className="text-muted">
              Source: {selectedVariable.Question_number || selectedVariable.name}
              {selectedVariable.type && ` (${selectedVariable.type})`}
            </small>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="oe-main-content">
        {/* Settings Box */}
        <div className="oe-settings-box">
          <h5>Settings</h5>
          
          {/* Checkboxes Row */}
          <div className="oe-input-checkboxes">
            <CheckboxWithTooltip
              checked={oeParameters?.isRequired || false}
              onChange={(e) => handleChange('isRequired', e.target.checked)}
              label="Required"
              tooltip="Make this field mandatory - respondents must provide an answer"
            />
            <CheckboxWithTooltip
              checked={oeParameters?.isDecimal || false}
              onChange={(e) => handleChange('isDecimal', e.target.checked)}
              label="Decimal"
              tooltip="It will change the format from number to decimals"
            />

            <CheckboxWithTooltip
              checked={oeParameters?.isSumTotal || false}
              onChange={(e) => handleChange('isSumTotal', e.target.checked)}
              label="Sum Total"
              tooltip="Display and validate the total sum of all numeric inputs"
            />

            <CheckboxWithTooltip
              checked={oeParameters?.isSum100 || false}
              onChange={(e) => handleChange('isSum100', e.target.checked)}
              label="Sum 100"
              tooltip="Ensure all numeric inputs add up to exactly 100"
            />
          </div>

          {/* Input Fields */}
          <div className="oe-input-grid">
            <Form.Group>
              <Form.Label className="oe-form-label">Size</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.size || "10"}
                onChange={(e) => handleChange('size', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Input size"
                className="oe-form-control-sm"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Input Type</Form.Label>
              <Form.Select
                size="sm"
                value={oeParameters?.inputType || "text"}
                onChange={(e) => handleChange('inputType', e.target.value)}
                className="oe-form-control-sm"
              >
                <option value="text">Text</option>
                <option value="email">Email</option>
                <option value="tel">Phone</option>
                <option value="url">URL</option>
                <option value="date">Date</option>
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Pre-text</Form.Label>
              <FormattableInput
                value={oeParameters?.preText || ""}
                onChange={(e) => handleChange('preText', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Text before input"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Post-text</Form.Label>
              <FormattableInput
                value={oeParameters?.postText || ""}
                onChange={(e) => handleChange('postText', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Text after input"
              />
            </Form.Group>
          </div>

          <div className="oe-numeric-grid">
            <Form.Group>
              <Form.Label className="oe-form-label">
                Termination Condition
              </Form.Label>
              <FormattableInput
                value={oeParameters?.term || ""}
                onChange={(e) => handleChange('term', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Enter condition"
              />
            </Form.Group>
            
            <Form.Group>
              <Form.Label className="oe-form-label">Exact Selection</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.exact || ""}
                onChange={(e) => handleChange('exact', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Exact Selection"
                className="oe-form-control-sm"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Minimum Value</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.min ?? ""}
                onChange={(e) => handleChange('min', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Min"
                className="oe-form-control-sm"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="oe-form-label">Maximum Value</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.max || ""}
                onChange={(e) => handleChange('max', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Max"
                className="oe-form-control-sm"
              />
            </Form.Group>
            
            <Form.Group>
              <Form.Label className="oe-form-label">Total Sum min Value</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.summin || ""}
                onChange={(e) => handleChange('summin', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Total Sum Min"
                className="oe-form-control-sm"
              />
            </Form.Group>
            
            <Form.Group>
              <Form.Label className="oe-form-label">Total Sum Max Value</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.summax || ""}
                onChange={(e) => handleChange('summax', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Total Sum Max"
                className="oe-form-control-sm"
              />
            </Form.Group>
            
            <Form.Group>
              <Form.Label className="oe-form-label">Digits in box</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.maxdigit || ""}
                onChange={(e) => handleChange('maxdigit', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="No of digits in box"
                className="oe-form-control-sm"
              />
            </Form.Group>
            
            <Form.Group>
              <Form.Label className="oe-form-label">Table Corner Text</Form.Label>
              <FormattableInput
                value={oeParameters?.tbCorner || ""}
                onChange={(e) => handleChange('tbCorner', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Text to show in table corner"
              />
            </Form.Group>
            
            <Form.Group>
              <Form.Label className="oe-form-label">Prefill</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.prefill || ""}
                onChange={(e) => handleChange('prefill', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Prefill value"
                className="oe-form-control-sm"
              />
            </Form.Group>
            <Form.Group className="oe-form-label-margin-bottom">
              <Form.Label className="oe-form-label">Number of Boxes</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.nboxes || ""}
                onChange={(e) => handleChange('nboxes', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Number of OE boxes"
                className="oe-form-control-sm"
              />
            </Form.Group>

          </div>
        </div>
        
        {/* General Settings and Validate - Side by Side */}
        <div className="oe-two-column-grid">
          {/* General Settings */}
          <div className="oe-settings-box oe-general-settings">
            <h5>General Settings</h5>
            {oeParameters?.nboxes > 0 && (
              <Form.Group className="oe-form-label-margin-bottom">
                <Form.Label className="oe-form-label">Compulsory no of boxes</Form.Label>
                <Form.Control
                  type="number"
                  value={oeParameters?.nboxesrequired || ""}
                  onChange={(e) => handleChange('nboxesrequired', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="Number of OE boxes to be filled compulsory"
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}
            
            {/* Checkboxes in two columns */}
            <div className="oe-checkbox-grid">
              <div className="oe-checkbox-column">
                <CheckboxWithTooltip
                  checked={oeParameters?.isHidden || false}
                  onChange={(e) => handleChange('isHidden', e.target.checked)}
                  label="Hidden Question"
                  tooltip="Make a Question Hidden"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isQuota || false}
                  onChange={(e) => handleChange('isQuota', e.target.checked)}
                  label="Quota Question"
                  tooltip="Make Question as Quota"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isNoanswer || false}
                  onChange={(e) => handleChange('isNoanswer', e.target.checked)}
                  label="No Answer for Decipher"
                  tooltip="This will append No Answer row for Decipher programming"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isRightLeft || false}
                  onChange={(e) => handleChange('isRightLeft', e.target.checked)}
                  label="Right Left Setup"
                  tooltip="This will make Right Left Combination of rows and columns along with scale provided in Columns reverse statement in Statements. Only applicable in Grids"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isCarousel || false}
                  onChange={(e) => handleChange('isCarousel', e.target.checked)}
                  label="Carousel Setup"
                  tooltip="This will make Carousel Setup"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isCol || false}
                  onChange={(e) => handleChange('isCol', e.target.checked)}
                  label="Column Layout"
                  tooltip="Make grid as Column selection"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isSinCol || false}
                  onChange={(e) => handleChange('isSinCol', e.target.checked)}
                  label="Show Rows as Columns"
                  tooltip="Show single response as columns"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isMaxDiff || false}
                  onChange={handleMaxDiffChange}
                  label="Max Diff Setup"
                  tooltip="Enable MaxDiff with balanced design or custom design import"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isWithoutMaxDiff || false}
                  onChange={(e) => handleChange('isWithoutMaxDiff', e.target.checked)}
                  label="Max Diff without Setup"
                  tooltip="Enable MaxDiff without excel setup"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isSlider || false}
                  onChange={(e) => handleChange('isSlider', e.target.checked)}
                  label="Slider"
                  tooltip="Display input as a slider control for numeric values"
                />

              </div>
              
              <div className="oe-checkbox-column">
                
                {/* Mutually exclusive ranking options */}
                <div className="mutually-exclusive-group">
                                  <CheckboxWithTooltip
                  checked={oeParameters?.isDrag || false}
                  onChange={(e) => handleChange('isDrag', e.target.checked)}
                  label="Drag and Drop"
                  tooltip="Enable drag and drop functionality for reordering items"
                />

                  <CheckboxWithTooltip
                    checked={oeParameters?.isDropDown || false}
                    onChange={(e) => handleChange('isDropDown', e.target.checked)}
                    label="Drop Down"
                    tooltip="Enable Drop down functionality for answers"
                  />
                  <CheckboxWithTooltip
                    checked={oeParameters?.isClick || false}
                    onChange={(e) => handleChange('isClick', e.target.checked)}
                    label="Click to Rank"
                    tooltip="Enable Clickable design for Rank Functionality in Confirmit"
                  />
                  <CheckboxWithTooltip
                    checked={oeParameters?.isNumberRank || false}
                    onChange={(e) => handleChange('isNumberRank', e.target.checked)}
                    label="Number to Rank"
                    tooltip="Enable Number design for Rank Functionality"
                  />
                </div>
                
                <CheckboxWithTooltip
                  checked={oeParameters?.isTextarea || false}
                  onChange={(e) => handleChange('isTextarea', e.target.checked)}
                  label="Textarea"
                  tooltip="Use a multi-line text area instead of single-line text input"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isDate || false}
                  onChange={(e) => handleChange('isDate', e.target.checked)}
                  label="Date Picker"
                  tooltip="Display a calendar date picker for date selection"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isPageBreak || false}
                  onChange={(e) => handleChange('isPageBreak', e.target.checked)}
                  label="Remove pagebreak after question"
                  tooltip="Remove suspend tag"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.istablelook || false}
                  onChange={(e) => handleChange('istablelook', e.target.checked)}
                  label="Show Numeric Question as Table"
                  tooltip="This will show the numeric question in table format"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.optional || false}
                  onChange={(e) => handleChange('optional', e.target.checked)}
                  label="Optional"
                  tooltip="This will make question non manadatory"
                />
              <CheckboxWithTooltip
                  checked={oeParameters?.mainFolder || false}
                  onChange={(e) => handleChange('mainFolder', e.target.checked)}
                  label="Main Folder Started"
                  tooltip="This will place all question after this in main folder in Confirmit"
                />
              </div>
            </div>

            {oeParameters?.isMaxDiff && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowMaxDiffModal(true)}
                className="mt-2"
              >
                <FontAwesomeIcon icon={faTable} className="me-1" />
                Configure MaxDiff Design
              </Button>
            )}

            {oeParameters?.isMaxDiff && oeParameters?.maxDiffDesign && (
              <div className="maxdiff-summary mt-2 p-2 bg-light rounded">
                <small>
                  <strong>MaxDiff Design:</strong><br />
                  Method: {oeParameters.maxDiffDesign.designMethod}<br />
                  Versions: {oeParameters.maxDiffDesign.actualVersions || oeParameters.maxDiffDesign.designVersions}<br />
                  Tasks per version: {oeParameters.maxDiffDesign.actualTasksPerVersion || oeParameters.maxDiffDesign.tasksPerRespondent}<br />
                  Items per set: {oeParameters.maxDiffDesign.actualItemsPerSet || oeParameters.maxDiffDesign.itemsPerSet}<br />
                  {oeParameters.maxDiffDesign.uniqueItemsUsed && (
                    <>Unique items: {oeParameters.maxDiffDesign.uniqueItemsUsed}<br /></>
                  )}
                  {oeParameters.maxDiffDesign.designMethod === 'pasted' && (
                    <span className="text-success">✓ Auto-detected from paste</span>
                  )}
                </small>
              </div>
            )}

            <MaxDiffConfigComponent
              oeParameters={oeParameters}
              setOeParameters={setOeParameters}
              onConfigUpdate={handleMaxDiffConfigUpdate}
              handleChange={handleChange}
              showMaxDiffModal={showMaxDiffModal}
              setShowMaxDiffModal={setShowMaxDiffModal}
              maxDiffDesign={maxDiffDesign}
              setMaxDiffDesign={setMaxDiffDesign}
            />
          </div>

          {/* Validate */}
          <div className="oe-settings-box">
            <h5>Validate</h5>
            <Form.Group className="oe-form-label-margin-bottom">
              <Form.Label className="oe-form-label">Validation logic</Form.Label>
              <FormattableInput
                value={oeParameters?.validationLogic || ""}
                onChange={(e) => handleChange('validationLogic', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Validation logic"
              />
            <Form.Label className="oe-form-label">Error Message</Form.Label>
              <FormattableInput
                value={oeParameters?.errorMessage || ""}
                onChange={(e) => handleChange('errorMessage', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Error Message if any"
              />
            </Form.Group>
            
            <div className="oe-validate-checkboxes">
              <CheckboxWithTooltip
                checked={oeParameters?.isSequential || false}
                onChange={(e) => handleChange('isSequential', e.target.checked)}
                label="Sequential"
                tooltip="Ensure responses are entered in sequential order"
              />
              <CheckboxWithTooltip
                checked={oeParameters?.isFirstComp || false}
                onChange={(e) => handleChange('isFirstComp', e.target.checked)}
                label="First Box Compulsory"
                tooltip="Ensure First Box is compulsory in Open end"
              />
              <CheckboxWithTooltip
                checked={oeParameters?.isDuplicate || false}
                onChange={(e) => handleChange('isDuplicate', e.target.checked)}
                label="Duplicate Entries Check"
                tooltip="Ensure to check the duplicate entries in Open end"
              />
            <Form.Group className="oe-form-label-margin-bottom">
              <Form.Label className="oe-form-label">Disable cells</Form.Label>
              <FormattableInput
                value={oeParameters?.disabledCells || ""}
                onChange={(e) => handleChange('disabledCells', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Disable Cells: R1C1, R2C3 R1 C4 etc"
              />
            </Form.Group>

            </div>
          </div>
        </div>
      </div>
      {JSON.stringify(selectedVariable)}
    </div>
  );
};

export default OESettingsComponent;